import {
  getCatalogue,
  getExperiments,
  mergeInventoryToAmalgam,
  mergeProductsIntoDefaultVariants,
  mergeProductsIntoNonDefaultVariants,
  mergeProductsToAmalgam,
  showInStockTag,
  showOnSaleTag
} from './Amalgam';
import {
  checkIfAllOnSaleInStockTagSelected,
  getSelectedTagsForApi,
  getVisibleTags,
  ON_SALE_TAG_KEY
} from './Tags';
import { getSearchTerm } from './Search';
import { getProduct } from './Product';
import { SINGLE_AMALGAM_PAGE_SIZE } from '../../config';
import {
  getCatalogueFilters,
  getFilteredPaginatedAmalgam as apiGetFilteredPaginatedAmalgam,
  getFilteredTaggedPaginatedAmalgam,
  getPaginatedAmalgam as apiGetPaginatedAmalgam,
  getTopProductsBasedOnTags
} from './Api';
import EventBus from './EventBus';
import cache from './cache';
import { logException, MORE_PRODUCTS_BASED_ON_TAG_COUNT } from './Utils';
import { includes } from '../../../utils/GlobalUtils';
import { getCatalogueFiltersCounts, getSelectedFilters } from './Filters';
import {
  formatFiltersForCatalogueView,
  formatFiltersToGetPaginatedAmalgam
} from '../organisms/CatalogueFilters/helpers';

let catalogueDescriptionExpanded = false;
let filteredListPage = 0;
let filteredProductList = [];
let filteredProductTotalCount = null;
let filteredNextPageAvailable = true;
let filteredTagsList = null;
let filteredShowOnSaleTag = null;
let filteredShowInStockTag = null;
const filteredRequest = {};
let catalogueFiltersPromise = null;
const cachedCatalogueFiltersObject = {};

export const getSearchFilteredProductKeys = (productIds) => {
  const searchTerm = getSearchTerm();

  if (!searchTerm || !window.Fuse) {
    return productIds;
  }

  let distance = 0;
  const products = productIds
    .map((id) => {
      const product = getProduct(id);

      if (!product) {
        return null;
      }

      if ((product.name || '').length > distance) {
        distance = product.name.length;
      }

      if ((product.description || '').length > distance) {
        distance = product.description.length;
      }

      return { id, name: product.name, description: product.description };
    })
    .filter((product) => product);

  const fuse = new window.Fuse(products, {
    keys: ['name', 'description'],
    id: 'id',
    distance
  });

  const textSearchResults = fuse.search(searchTerm);

  const priceSearchResults = [];
  if (!Number.isNaN(searchTerm)) {
    const searchPrice = parseFloat(searchTerm);

    productIds.forEach((productId) => {
      const product = getProduct(productId);
      if (!product) {
        return;
      }
      if (
        (typeof product.discounted_price === 'number' &&
          product.discounted_price === searchPrice) ||
        (typeof product.price === 'number' && product.price === searchPrice)
      ) {
        priceSearchResults.push(productId);
      }
    });
  }

  return [...priceSearchResults, ...textSearchResults].filter(
    (productId, index, resultsArray) => resultsArray.indexOf(productId) === index
  );
};

export const getCatalogueId = () => getCatalogue().id;
export const getCatalogueTitle = () => getCatalogue().title;
export const getCatalogueTags = () => {
  if (getSearchTerm()) {
    return Array.from(
      new Set([...(filteredTagsList || []), ...(getSelectedTagsForApi() || [])])
    ).sort();
  }
  return getCatalogue().tagsList || [];
};
export const getProductList = () => getCatalogue().productList || {};
export const getCatalogueDescription = () => getCatalogue().description || '';
export const getCatalogueFormattedDescription = () => getCatalogue().formattedDescription || '';
export const getAllProductIds = () => {
  if (shouldUseFilteredList()) {
    return filteredProductList;
  }
  const productList = getProductList();

  return Object.keys(productList).sort((a, b) => (productList[a] > productList[b] ? 1 : -1));
};
export const getSortedProductKeys = ({ aroundProductId } = {}) => {
  const allProductIds = getAllProductIds();

  let startAtIndex = 0;
  let endAtIndex;

  if (aroundProductId) {
    const aroundProductIdIndex = allProductIds.indexOf(aroundProductId);
    for (startAtIndex = aroundProductIdIndex - 1; startAtIndex >= 0; startAtIndex -= 1) {
      if (!getProduct(allProductIds[startAtIndex])) {
        startAtIndex = startAtIndex + 1;
        break;
      }
    }
  }

  if (startAtIndex < 0) {
    startAtIndex = 0;
  }

  for (endAtIndex = startAtIndex; endAtIndex < allProductIds.length; endAtIndex += 1) {
    if (!getProduct(allProductIds[endAtIndex])) {
      endAtIndex = endAtIndex - 1;
      break;
    }
  }

  return allProductIds.slice(startAtIndex, endAtIndex + 1);
};

export const shouldUseFilteredList = () => {
  const searchTerm = getSearchTerm();
  const filters = getSelectedFilters();

  return !!(searchTerm || getCatalogueFiltersCounts(filters) > 0);
};

export const getSelectedSortedProductKeys = ({ aroundProductId } = {}) =>
  shouldUseFilteredList() ? [...filteredProductList] : getSortedProductKeys({ aroundProductId });
export const getFilteredProductCount = () => filteredProductTotalCount;

export const expandCatalogueDescription = () => {
  catalogueDescriptionExpanded = true;
};

export const isCatalogueDescriptionExpanded = () => catalogueDescriptionExpanded;

export const getCachedCatalogueFilters = () => cachedCatalogueFiltersObject[getCatalogueId()];

export const setCachedCatalogueFilters = (value) => {
  cachedCatalogueFiltersObject[getCatalogueId()] = value;
};

export const fetchAndCacheCatalogueFilters = () => {
  if (getCachedCatalogueFilters()) {
    return Promise.resolve(getCachedCatalogueFilters());
  }

  // if already made api call
  if (catalogueFiltersPromise !== null) {
    return catalogueFiltersPromise;
  }
  catalogueFiltersPromise = getCatalogueFilters()
    .then((filtersResponse) => {
      setCachedCatalogueFilters(filtersResponse);
      catalogueFiltersPromise = null;
      return filtersResponse;
    })
    .catch(() => {
      catalogueFiltersPromise = null;
      return getCachedCatalogueFilters();
    });

  return catalogueFiltersPromise;
};

export const doTagsExist = () => {
  if (onSaleTagExists() || inStockTagExists()) {
    return true;
  }

  const tags = getVisibleTags();

  return !!(tags && tags.length);
};

export const doCatalogueFiltersExists = () => {
  const cachedCatalogueFilters = getCachedCatalogueFilters();
  const tags = getVisibleTags();
  if (tags && tags.length) {
    return true;
  }
  return !!cachedCatalogueFilters && !!formatFiltersForCatalogueView(cachedCatalogueFilters).length;
};

export const isNextPageAvailable = ({ afterProductId } = {}) => {
  if (shouldUseFilteredList()) {
    return filteredNextPageAvailable;
  }

  let productIds = getAllProductIds();
  if (afterProductId) {
    productIds = productIds.slice(productIds.indexOf(afterProductId) + 1);
  }
  const renderedProductIds = productIds.filter((productId) => getProduct(productId));
  return renderedProductIds.length !== productIds.length;
};

export const isPrevPageAvailable = ({ beforeProductId } = {}) => {
  if (shouldUseFilteredList()) {
    return false;
  }

  let productIds = getAllProductIds();
  if (beforeProductId) {
    productIds = productIds.slice(0, productIds.indexOf(beforeProductId));
  }
  const notRenderedProduct = productIds.find((productId) => !getProduct(productId));
  return !!notRenderedProduct;
};

export const getCatalogueAccessibilityProps = ({ title }) => {
  if (title) {
    return {
      role: 'img',
      ariaLabel: title,
      title
    };
  }
  return {};
};

const getPrevPageProductIds = ({ beforeProductId } = {}) => {
  const allProductIds = getAllProductIds();

  let endAt = allProductIds.length - 1;
  if (beforeProductId) {
    endAt = allProductIds.indexOf(beforeProductId) - 1;
  }
  const productIdsToFetchAmalgam = [];
  for (; endAt >= 0; endAt -= 1) {
    if (!getProduct(allProductIds[endAt])) {
      productIdsToFetchAmalgam.push(allProductIds[endAt]);
    }
    if (SINGLE_AMALGAM_PAGE_SIZE <= productIdsToFetchAmalgam.length) {
      break;
    }
  }

  return productIdsToFetchAmalgam;
};

const getNextPageProductIds = ({ afterProductId } = {}) => {
  const allProductIds = getAllProductIds();

  let startAt = 0;
  if (afterProductId) {
    startAt = allProductIds.indexOf(afterProductId);
  }
  const productIdsToFetchAmalgam = [];
  for (; startAt < allProductIds.length; startAt += 1) {
    if (!getProduct(allProductIds[startAt])) {
      productIdsToFetchAmalgam.push(allProductIds[startAt]);
    }
    if (SINGLE_AMALGAM_PAGE_SIZE <= productIdsToFetchAmalgam.length) {
      break;
    }
  }

  return productIdsToFetchAmalgam;
};

export const getPaginatedAmalgam = ({ afterProductId, beforeProductId } = {}) => {
  const productIds = beforeProductId
    ? getPrevPageProductIds({ beforeProductId })
    : getNextPageProductIds({ afterProductId });

  if (!productIds.length) {
    return Promise.resolve();
  }

  return apiGetPaginatedAmalgam(productIds).then(({ products, inventory }) => {
    mergeProductsToAmalgam(products);
    mergeInventoryToAmalgam(inventory);
    mergeProductsIntoDefaultVariants();
    mergeProductsIntoNonDefaultVariants();
    EventBus.notify(EventBus.TYPE.AMALGAM_PAGE_FETCHED);
    return Promise.resolve();
  });
};

const getFilteredPaginatedAmalgam = ({
  searchTerm = null,
  filters = null,
  cancelPreviousRequest = true
}) => {
  if (cancelPreviousRequest && filteredRequest && filteredRequest.cancel) {
    filteredRequest.cancel();
  }

  if (!filteredNextPageAvailable) {
    return Promise.resolve();
  }

  filteredListPage += 1;
  // adding "filteredNextPageAvailable = false" so that if cancelPreviousRequest is false
  // it can avoid multiple api calls
  // to make sure we get correct data after second api call use cancelPreviousRequest : true
  filteredNextPageAvailable = false;

  return new Promise((resolve, reject) => {
    const { onSaleTagSelected, inStockTagSelected } = checkIfAllOnSaleInStockTagSelected();
    apiGetFilteredPaginatedAmalgam({
      onSaleTagSelected,
      inStockTagSelected,
      searchTerm,
      filters: formatFiltersToGetPaginatedAmalgam(filters),
      page: filteredListPage,
      returnAggregatedTags: !!searchTerm,
      catalogueShowOutOfStock: !!getExperiments().showOutOfStockProduct,
      filteredRequest
    })
      .then(
        ({
          productIds,
          products,
          inventory,
          totalProducts,
          tagsList,
          showOnSaleTag,
          showInStockTag
        }) => {
          if (getSearchTerm() !== searchTerm) {
            resolve();
            return;
          }

          filteredProductList.push(...productIds);
          filteredProductTotalCount = totalProducts;
          filteredNextPageAvailable =
            filteredProductList.length === totalProducts ? false : !!productIds.length;
          if (searchTerm) {
            filteredTagsList = tagsList;
            filteredShowOnSaleTag = showOnSaleTag;
            filteredShowInStockTag = showInStockTag;
          }
          mergeProductsToAmalgam(products);
          mergeInventoryToAmalgam(inventory);
          mergeProductsIntoDefaultVariants();
          mergeProductsIntoNonDefaultVariants();
          if (filteredListPage === 1) {
            EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
          } else {
            EventBus.notify(EventBus.TYPE.AMALGAM_PAGE_FETCHED);
          }

          resolve();
        }
      )
      .catch((err) => {
        if (err && err.message === 'Cancelled') {
          resolve();
        } else {
          reject(err);
        }
      });
  });
};

export const getNextPage = ({ afterProductId, beforeProductId, cancelPreviousRequest } = {}) => {
  if (shouldUseFilteredList()) {
    return getFilteredPaginatedAmalgam({
      searchTerm: getSearchTerm(),
      filters: getSelectedFilters(),
      cancelPreviousRequest
    });
  }

  return getPaginatedAmalgam({ afterProductId, beforeProductId });
};

export const onSaleTagExists = () => (getSearchTerm() ? filteredShowOnSaleTag : showOnSaleTag());
export const inStockTagExists = () => (getSearchTerm() ? filteredShowInStockTag : showInStockTag());

export const fetchTopProductsBasedOnTags = ({
  tagIdsList,
  productId,
  catalogueId,
  onSaleTagSelected
}) =>
  getTopProductsBasedOnTags({
    tagIdsList,
    onSaleTagSelected,
    catalogueId,
    catalogueShowOutOfStock: !!getExperiments().showOutOfStockProduct,
    excludeProductData: {
      id: productId
    }
  }).then(({ productsObject, inventory, tagMappedProducts }) => {
    mergeProductsToAmalgam(productsObject);
    mergeInventoryToAmalgam(inventory);
    mergeProductsIntoDefaultVariants();
    mergeProductsIntoNonDefaultVariants();
    return Promise.resolve(tagMappedProducts);
  });

export const getNextPageProductsForTag = ({
  productId,
  catalogueId,
  from,
  lastProductPosition,
  tag
}) => {
  const onSaleTagSelected = tag === ON_SALE_TAG_KEY;
  return new Promise((resolve, reject) => {
    getFilteredTaggedPaginatedAmalgam({
      catalogueId,
      selectedTag: onSaleTagSelected ? null : tag,
      pageSize: MORE_PRODUCTS_BASED_ON_TAG_COUNT,
      onSaleTagSelected,
      catalogueShowOutOfStock: !!getExperiments().showOutOfStockProduct,
      excludeProductData: {
        id: productId
      },
      lastProductPosition,
      from
    })
      .then(({ productIds, products, inventory, nextFrom, nextProductPosition }) => {
        mergeProductsToAmalgam(products);
        mergeInventoryToAmalgam(inventory);
        mergeProductsIntoDefaultVariants();
        mergeProductsIntoNonDefaultVariants();
        resolve({
          productIds,
          nextFromPos: nextFrom,
          nextProductPos: nextProductPosition
        });
      })
      .catch((err) => {
        if (err && err.message === 'Cancelled') {
          resolve();
        } else {
          reject(err);
        }
      });
  });
};

export const fetchProductsData = (productIds) =>
  apiGetPaginatedAmalgam(productIds)
    .then(({ products, inventory }) => {
      mergeProductsToAmalgam(products);
      mergeInventoryToAmalgam(inventory);
      mergeProductsIntoDefaultVariants();
      mergeProductsIntoNonDefaultVariants();
      const productsDataNotFound = productIds.filter(
        (productId) => !includes(Object.keys(products), productId)
      );
      if (productsDataNotFound.length > 0) {
        logException('Products data not fetched', {
          productIds: productsDataNotFound
        });
      }
      return Promise.resolve();
    })
    .catch((err) => {
      if (!err || err.message !== 'Cancelled') {
        return Promise.reject(err);
      }
      return Promise.resolve();
    });

const onSelectedTagChange = () => {
  filteredListPage = 0;
  filteredProductList = [];
  filteredProductTotalCount = null;
  filteredNextPageAvailable = true;

  const searchTerm = getSearchTerm();
  const filters = getSelectedFilters();

  if (!searchTerm && !getCatalogueFiltersCounts(filters)) {
    EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
    return;
  }

  EventBus.notify(EventBus.TYPE.TAG_FILTERING_STARTED);
  const finalCallback = () => {
    EventBus.notify(EventBus.TYPE.TAG_FILTERING_DONE);
    EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
  };
  getFilteredPaginatedAmalgam({
    searchTerm,
    filters
  })
    .then(finalCallback)
    .catch(finalCallback);
};

const onClearFilter = () => {
  filteredListPage = 0;
  filteredProductList = [];
  filteredProductTotalCount = null;
  filteredNextPageAvailable = true;

  const searchTerm = getSearchTerm();
  const filters = getSelectedFilters();

  if (!searchTerm && !getCatalogueFiltersCounts(filters)) {
    EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
    return;
  }

  EventBus.notify(EventBus.TYPE.CLEAR_FILTER_STARTED);
  getFilteredPaginatedAmalgam({
    filters,
    searchTerm
  })
    .then(() => EventBus.notify(EventBus.TYPE.CLEAR_FILTER_DONE))
    .catch(() => EventBus.notify(EventBus.TYPE.CLEAR_FILTER_DONE));
};

const onSearchTermChange = () => {
  filteredListPage = 0;
  filteredProductList = [];
  filteredProductTotalCount = null;
  filteredNextPageAvailable = true;
  filteredTagsList = null;

  const searchTerm = getSearchTerm();
  const filters = getSelectedFilters();

  if (!searchTerm && !getCatalogueFiltersCounts(filters)) {
    EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
    EventBus.notify(EventBus.TYPE.SEARCH_DONE);
    return;
  }

  EventBus.notify(EventBus.TYPE.SEARCH_STARTED);
  getFilteredPaginatedAmalgam({ searchTerm, filters })
    .then(() => {
      if (searchTerm === getSearchTerm()) {
        EventBus.notify(EventBus.TYPE.SEARCH_DONE);
      }
    })
    .catch(() => {
      if (searchTerm === getSearchTerm()) {
        EventBus.notify(EventBus.TYPE.SEARCH_DONE);
      }
    });
};

const onChangeFiltersSelection = () => {
  filteredListPage = 0;
  filteredProductList = [];
  filteredProductTotalCount = null;
  filteredNextPageAvailable = true;
  filteredTagsList = null;

  const searchTerm = getSearchTerm();
  const filters = getSelectedFilters();

  const onCompleteCallback = () => EventBus.notify(EventBus.TYPE.CATALOGUE_FILTERS_CHANGED_DONE);

  if (!searchTerm && !getCatalogueFiltersCounts(filters)) {
    onCompleteCallback();
    EventBus.notify(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE);
    return;
  }

  getFilteredPaginatedAmalgam({ searchTerm, filters })
    .then(onCompleteCallback)
    .catch(onCompleteCallback);
};

const onInitialSearchTagsFilterDone = () => {
  cache.clearGridRowHeightAndNameHeightCache();
};

EventBus.addListener(EventBus.TYPE.SELECTED_TAG_CHANGED, onSelectedTagChange);
EventBus.addListener(EventBus.TYPE.CLEAR_FILTER, onClearFilter);
EventBus.addListener(EventBus.TYPE.SEARCH_TERM_CHANGED, onSearchTermChange);
EventBus.addListener(EventBus.TYPE.CATALOGUE_FILTERS_CHANGED, onChangeFiltersSelection);
EventBus.addListener(EventBus.TYPE.INITIAL_SEARCH_TAGS_FILTER_DONE, onInitialSearchTagsFilterDone);
